import { DrofusChangeEventMetadata } from "./DrofusChangeEventDetails";
import { DrofusChangesLoadingOverlay } from "./DrofusChangesLoadingOverlay";
import "ag-grid-enterprise";
import {
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Pagination,
  Select,
  SimpleGrid,
  Table,
  Text,
} from "@mantine/core";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams, ValueFormatterParams } from "ag-grid-enterprise";
import { useMemo, useRef, useState } from "react";
import { FaFileExcel } from "react-icons/fa6";
import { useAppDispatch } from "../app/hooks";
import { useAgGridReduxState } from "../hooks/useAgGridReduxState";
import "./DrofusEndringerTable.css";
import { presentChanges, valuePreview, type PresentedChange } from "./drofusChangePresentation";
import { formatDrofusChangeReason, getDrofusChangeReason } from "./drofusChangeReason";
import { DrofusDisciplineBadge } from "../components/DrofusDisciplineBadge";
import { setSelectedRoomTabInRedux, setSelectedSpaceNumberInRedux } from "../DesignTab/designSlice";
import { RoomSideBarTabs } from "@multiconsult-group/multiplacer-core";
import { openDrofusEndringerOccurrenceDetails } from "./drofusEndringerSlice";

type DrofusEndringRow = PresentedChange;

const changeTypeLabel: Record<DrofusEndringRow["changeType"], string> = {
  updated: "Endret",
  added: "Ny",
  removed: "Fjernet",
};

const changeTypeColor: Record<DrofusEndringRow["changeType"], string> = {
  updated: "yellow",
  added: "green",
  removed: "red",
};

const defaultColDef: ColDef<DrofusEndringRow> = {
  sortable: false,
  filter: false,
  resizable: true,
  minWidth: 80,
  tooltipValueGetter: ({ value }) => formatValue(value),
};

const columnsOnlySideBar = {
  toolPanels: [
    {
      id: "columns",
      labelDefault: "Kolonner",
      labelKey: "columns",
      iconKey: "columns",
      toolPanel: "agColumnsToolPanel",
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
      },
    },
  ],
};

function formatDateTime(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("nb-NO", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function formatValue(value: unknown): string {
  if (value == null || value === "") return "-";
  if (typeof value === "boolean") return value ? "Ja" : "Nei";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return JSON.stringify(value);
}

function getOccurrenceId(row?: DrofusEndringRow): number | null {
  if (!row || row.entityType !== "occurrence") return null;
  if (typeof row.entityId === "number") return row.entityId;

  const numericId = Number(row.entityId);
  return Number.isInteger(numericId) ? numericId : null;
}

function getChangedBy(row?: DrofusEndringRow): string {
  return row?.drofusLog?.username || "-";
}

function getChangedAt(row?: DrofusEndringRow): string {
  return formatDateTime(row?.drofusLog?.time) || "-";
}

const ChangeReason: React.FC<{ row: DrofusEndringRow; showExplanation?: boolean }> = ({
  row,
  showExplanation,
}) => {
  const reason = getDrofusChangeReason(row);
  return (
    <Box py={6} style={{ lineHeight: 1.4, whiteSpace: "normal", overflowWrap: "anywhere" }}>
      <Text size="sm" fw={600}>
        {row.roomChanges
          ? `Flyttet fra ${row.moveFrom} til ${row.moveTo}`
          : reason.title.replace(/ endret$/, "")}
      </Text>
      <Group gap={4} mt={4}>
        {reason.disciplines.map(({ discipline }) => (
          <DrofusDisciplineBadge key={discipline} discipline={discipline} />
        ))}
        <Text size="xs" c="dimmed">
          {row.roomChanges ||
          reason.disciplines.some((item) => item.reason.startsWith("Felles felt"))
            ? "Felles felt"
            : reason.disciplines.some((item) => item.reason === "Fagets behovsfelt endret")
              ? "Behovsfelt"
              : ""}
        </Text>
      </Group>
      {reason.disciplines.length === 0 && (
        <Text size="xs" c="dimmed">
          Ingen berørte fag registrert
        </Text>
      )}
      {showExplanation && (
        <Text size="sm" c="dimmed" mt="xs">
          {reason.explanation}
        </Text>
      )}
    </Box>
  );
};

const ChangeReasonRenderer: React.FC<ICellRendererParams<DrofusEndringRow>> = ({ data }) =>
  data ? <ChangeReason row={data} /> : null;

const ChangeValueRenderer: React.FC<ICellRendererParams<DrofusEndringRow>> = ({ data, colDef }) =>
  data ? (
    <div className="drofus-change-value">
      <span className="drofus-change-value-preview">
        {valuePreview(data, colDef?.field === "oldValue" ? "oldValue" : "newValue")}
      </span>
    </div>
  ) : null;

const DetailsRenderer: React.FC<
  ICellRendererParams<DrofusEndringRow> & { onOpenComparison: (row: DrofusEndringRow) => void }
> = ({ data, onOpenComparison }) =>
  data ? (
    <button type="button" className="drofus-change-details" onClick={() => onOpenComparison(data)}>
      {data.roomChanges ? `${data.roomChanges.length} romfelt` : "Detaljer"}
    </button>
  ) : null;

const ArticleRenderer: React.FC<
  ICellRendererParams<DrofusEndringRow> & {
    onOpenDetails: (row: DrofusEndringRow, id: number) => void;
  }
> = ({ data, onOpenDetails }) =>
  data ? (
    <Box py={6} style={{ lineHeight: 1.4, whiteSpace: "normal", overflowWrap: "anywhere" }}>
      <Text size="sm" fw={500}>
        {data.context.article?.name ?? "Ukjent artikkel"}
      </Text>
      <Text size="xs" c="dimmed">
        {data.context.article?.compCode} · Forekomst-ID:{" "}
        <OccurrenceIdRenderer
          data={data}
          value={getOccurrenceId(data)}
          onOpenDetails={onOpenDetails}
        />
      </Text>
    </Box>
  ) : null;

const MoveDetailsRenderer: React.FC<{ data?: DrofusEndringRow }> = ({ data }) => (
  <Box p="md">
    <Text size="xs" c="dimmed" mb="xs">
      Romfelt på denne siden
    </Text>
    <Table style={{ tableLayout: "fixed" }}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Endret felt</Table.Th>
          <Table.Th>Gammel verdi</Table.Th>
          <Table.Th>Ny verdi</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {data?.roomChanges?.map((change) => (
          <Table.Tr key={change.changeKey}>
            <Table.Td>{change.displayName ?? change.normalizedPath}</Table.Td>
            <Table.Td style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {valuePreview(change, "oldValue")}
            </Table.Td>
            <Table.Td style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {valuePreview(change, "newValue")}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  </Box>
);

const ChangeTypeRenderer: React.FC<
  ICellRendererParams<DrofusEndringRow, DrofusEndringRow["changeType"]>
> = ({ value }) => {
  if (!value) return null;
  return (
    <Badge
      size="md"
      radius="sm"
      color={changeTypeColor[value]}
      variant="light"
      styles={{
        root: {
          fontSize: 13,
          fontWeight: 600,
          textTransform: "none",
          letterSpacing: 0,
          ...(value === "updated"
            ? {
                color: "#704600",
                backgroundColor: "#fff3bf",
                border: "1px solid #e8ca70",
              }
            : {}),
        },
      }}
    >
      {changeTypeLabel[value]}
    </Badge>
  );
};

const DisciplineRenderer: React.FC<ICellRendererParams<DrofusEndringRow, string[]>> = ({
  value,
}) => {
  if (!Array.isArray(value) || value.length === 0) return null;
  return (
    <Group gap={4} wrap="nowrap">
      {value.map((discipline) => (
        <DrofusDisciplineBadge key={discipline} discipline={discipline} />
      ))}
    </Group>
  );
};

const OccurrenceIdRenderer: React.FC<
  { data?: DrofusEndringRow; value?: number | null } & {
    onOpenDetails?: (row: DrofusEndringRow, occurrenceId: number) => void;
  }
> = ({ value, data, onOpenDetails }) => {
  if (!data || !value) {
    return (
      <Text size="sm" c="dimmed">
        -
      </Text>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpenDetails?.(data, value)}
      style={{
        background: "transparent",
        border: 0,
        color: "#1971c2",
        cursor: "pointer",
        font: "inherit",
        fontWeight: 700,
        padding: 0,
        textDecoration: "underline",
      }}
    >
      {value}
    </button>
  );
};

const RoomNumberRenderer: React.FC<
  ICellRendererParams<DrofusEndringRow, string | null> & {
    onOpenRoom?: (roomNumber: string) => void;
  }
> = ({ value, onOpenRoom }) => {
  if (!value) {
    return (
      <Text size="sm" c="dimmed">
        -
      </Text>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpenRoom?.(value)}
      style={{
        background: "transparent",
        border: 0,
        color: "#1971c2",
        cursor: "pointer",
        font: "inherit",
        fontWeight: 700,
        padding: 0,
        textDecoration: "underline",
      }}
    >
      {value}
    </button>
  );
};

export interface DrofusOccurrenceChangesTableProps {
  items: PresentedChange[];
  total: number;
  offset: number;
  limit: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  enabled: boolean;
  stateKey: string;
  showRoomColumn?: boolean;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  onRetry: () => void;
}

export const DrofusOccurrenceChangesTable: React.FC<DrofusOccurrenceChangesTableProps> = ({
  items,
  total,
  offset,
  limit,
  isLoading,
  isFetching,
  isError,
  enabled,
  stateKey,
  showRoomColumn = true,
  onPageChange,
  onLimitChange,
  onRetry,
}) => {
  const dispatch = useAppDispatch();
  const [comparison, setComparison] = useState<DrofusEndringRow | null>(null);
  const gridRef = useRef<AgGridReact<DrofusEndringRow>>(null);
  const rowData = useMemo(() => presentChanges(items), [items]);
  const pagination = {
    limit,
    total,
    firstVisibleRow: total === 0 ? 0 : offset + 1,
    lastVisibleRow: Math.min(offset + items.length, total),
    totalPages: Math.max(1, Math.ceil(total / limit)),
    activePage: Math.floor(offset / limit) + 1,
  };

  const columnDefs = useMemo<ColDef<DrofusEndringRow>[]>(
    () => [
      {
        headerName: "Artikkel / forekomst",
        colId: "articleName",
        width: 330,
        minWidth: 180,
        autoHeight: true,
        valueGetter: ({ data }) => data?.context.article?.name,
        cellRenderer: "ArticleRenderer",
        cellRendererParams: {
          onOpenDetails: (row: DrofusEndringRow, occurrenceId: number) =>
            dispatch(
              openDrofusEndringerOccurrenceDetails({
                changeEvent: row,
                projectNo: row.projectNo,
                runId: row.runId,
                occurrenceId,
                ...(row.changeType === "removed"
                  ? {
                      removedChange: {
                        context: row.context,
                        runStartedAt: row.runStartedAt,
                        affectedDisciplines: row.affectedDisciplines,
                        drofusLog: row.drofusLog,
                      },
                    }
                  : {}),
              })
            ),
        },
      },
      {
        headerName: "Romnummer",
        colId: "roomFunctionNo",
        initialHide: !showRoomColumn,
        width: 140,
        valueGetter: ({ data }) => data?.context.room?.functionNo,
        cellRenderer: "RoomNumberRenderer",
        cellRendererParams: {
          onOpenRoom: (roomNumber: string) => {
            dispatch(setSelectedRoomTabInRedux(RoomSideBarTabs.ROOM_DROFUS_CHANGES));
            dispatch(setSelectedSpaceNumberInRedux(roomNumber));
          },
        },
      },
      {
        headerName: "Endret felt",
        field: "displayName",
        width: 280,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: { innerRenderer: "ChangeReasonRenderer", suppressCount: true },
        tooltipValueGetter: ({ data }) => (data ? formatDrofusChangeReason(data) : ""),
        wrapText: true,
        autoHeight: true,
        valueGetter: ({ data }) => data?.displayName ?? data?.normalizedPath ?? data?.changeKey,
      },
      {
        headerName: "Gammel verdi",
        field: "oldValue",
        width: 260,
        minWidth: 140,
        autoHeight: true,
        cellClass: "drofus-change-old",
        headerClass: "drofus-change-old",
        cellRenderer: "ChangeValueRenderer",
        valueFormatter: ({ value }) => formatValue(value),
      },
      {
        headerName: "Ny verdi",
        field: "newValue",
        width: 260,
        minWidth: 140,
        autoHeight: true,
        cellClass: "drofus-change-new",
        headerClass: "drofus-change-new",
        cellRenderer: "ChangeValueRenderer",
        valueFormatter: ({ value }) => formatValue(value),
      },
      {
        headerName: "",
        colId: "details",
        width: 100,
        cellRenderer: "DetailsRenderer",
        cellRendererParams: { onOpenComparison: setComparison },
      },
      {
        headerName: "Type",
        field: "changeType",
        width: 120,
        cellRenderer: "ChangeTypeRenderer",
      },
      {
        headerName: "Oppdaget",
        headerTooltip: "Tidspunktet da innlesningen oppdaget endringen.",
        field: "runStartedAt",
        width: 150,
        valueFormatter: ({ value }: ValueFormatterParams<DrofusEndringRow, string>) =>
          formatDateTime(value),
      },
      {
        headerName: "Bruker",
        colId: "changedBy",
        width: 130,
        valueGetter: ({ data }) => getChangedBy(data),
      },
      {
        headerName: "Loggdato",
        headerTooltip: "Tidspunkt i tilknyttet dRofus-logg; ikke nødvendigvis for dette feltet.",
        colId: "drofusLogTime",
        width: 150,
        valueGetter: ({ data }) => getChangedAt(data),
      },
      {
        headerName: "Fag",
        field: "affectedDisciplines",
        width: 150,
        cellRenderer: "DisciplineRenderer",
        valueFormatter: ({ value }: ValueFormatterParams<DrofusEndringRow, string[]>) =>
          Array.isArray(value) ? value.join(", ") : "",
      },
      {
        headerName: "Romnavn",
        colId: "roomName",
        width: 180,
        valueGetter: ({ data }) => data?.context.room?.fullName,
      },
      {
        headerName: "Bygg",
        field: "roomBuilding",
        width: 110,
      },
      {
        headerName: "Etasje",
        field: "roomFloor",
        width: 130,
      },
      {
        headerName: "Artikkelnr",
        colId: "compCode",
        width: 140,
        valueGetter: ({ data }) => data?.context.article?.compCode,
      },
    ],
    [dispatch, showRoomColumn]
  );

  const availableColIds = useMemo(
    () => columnDefs.map((column) => (column.colId ?? column.field)!),
    [columnDefs]
  );
  const { gridStateHandlers } = useAgGridReduxState(stateKey, {
    availableColIds,
    columnsSignature: "compact-v2|" + availableColIds.join("|"),
  });

  const handleExportExcel = () => {
    gridRef.current?.api.exportDataAsExcel({
      fileName: `drofus-endringer-${new Date().toISOString().slice(0, 10)}.xlsx`,
      sheetName: "Endringer",
      processCellCallback: (params) => {
        const colId = params.column.getColId();

        const row = params.node?.data as DrofusEndringRow | undefined;
        if (colId === "details") return "";
        if (colId === "articleName" && row)
          return `${row.context.article?.name ?? ""} · ${getOccurrenceId(row) ?? ""}`;
        if (row?.roomChanges && ["displayName", "oldValue", "newValue"].includes(colId)) {
          return row.roomChanges
            .map(
              (item) =>
                `${item.displayName ?? item.normalizedPath}: ${colId === "displayName" ? formatDrofusChangeReason(item) : formatValue(item[colId as "oldValue" | "newValue"])}`
            )
            .join("\n");
        }
        if (colId === "displayName" && params.node?.data)
          return formatDrofusChangeReason(params.node.data);
        if (colId === "runStartedAt") return formatDateTime(params.value);
        if (colId === "drofusLogTime") return params.value || "-";
        if (colId === "changeType" && params.value in changeTypeLabel) {
          return changeTypeLabel[params.value as DrofusEndringRow["changeType"]];
        }
        if (colId === "affectedDisciplines" && Array.isArray(params.value)) {
          return params.value.join(", ");
        }
        if (colId === "changedBy") return params.value || "-";

        return formatValue(params.value);
      },
    });
  };

  return (
    <Box h="100%" w="100%" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Modal
        opened={comparison != null}
        onClose={() => setComparison(null)}
        title="Sammenlign endring"
        size="xl"
      >
        {comparison && (
          <>
            <ChangeReason row={comparison} showExplanation />
            <Text size="sm" c="dimmed" mb="md">
              Rom: {comparison.context.room?.functionNo ?? "-"} · Forekomst:{" "}
              {getOccurrenceId(comparison) ?? "-"}
            </Text>
            {comparison.roomChanges && (
              <Text size="xs" c="dimmed" mb="sm">
                {comparison.roomChanges.length} romfelt på denne siden. Andre feltendringer vises
                som egne rader.
              </Text>
            )}
            {(comparison.roomChanges ?? [comparison]).map((change) => (
              <Box key={change.changeKey} mb="md">
                {comparison.roomChanges && (
                  <Text fw={600} mb="xs">
                    {change.displayName ?? change.normalizedPath}
                  </Text>
                )}
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  {(["oldValue", "newValue"] as const).map((field) => (
                    <Box
                      key={field}
                      className={field === "oldValue" ? "drofus-change-old" : "drofus-change-new"}
                      p="sm"
                      style={{ minWidth: 0 }}
                    >
                      <Text fw={600} mb="xs">
                        {field === "oldValue" ? "Gammel verdi" : "Ny verdi"}
                      </Text>
                      <Box
                        component="pre"
                        style={{
                          margin: 0,
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                          fontSize: 13,
                        }}
                      >
                        {typeof change[field] === "object" && change[field] != null
                          ? JSON.stringify(change[field], null, 2)
                          : formatValue(change[field])}
                      </Box>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            ))}
            <DrofusChangeEventMetadata event={comparison} />
          </>
        )}
      </Modal>

      <Group justify="space-between" align="center" mb="xs" wrap="nowrap">
        <Text size="sm" c={isError ? "red" : "dimmed"} style={{ whiteSpace: "nowrap" }}>
          {isError
            ? "Kunne ikke laste endringer"
            : `${pagination.firstVisibleRow}-${pagination.lastVisibleRow} av ${pagination.total} feltendringer · ${rowData.length} rader`}
        </Text>
        <Group gap="xs" wrap="nowrap">
          {isError && (
            <Button size="xs" variant="light" disabled={!enabled || isFetching} onClick={onRetry}>
              Prøv igjen
            </Button>
          )}
          {isFetching && (
            <Badge variant="light" color="blue" radius="sm">
              Laster
            </Badge>
          )}
          <Button
            size="xs"
            color="green"
            variant="light"
            radius="sm"
            leftSection={<FaFileExcel size={14} />}
            disabled={!enabled || isLoading || rowData.length === 0}
            onClick={handleExportExcel}
          >
            Excel
          </Button>
        </Group>
      </Group>

      <Box
        className="ag-theme-quartz drofus-changes-grid"
        style={{ flex: 1, minHeight: 0, width: "100%" }}
      >
        <AgGridReact<DrofusEndringRow>
          ref={gridRef}
          {...gridStateHandlers}
          enableCellTextSelection
          ensureDomOrder
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          loading={isLoading || isFetching}
          loadingOverlayComponent={DrofusChangesLoadingOverlay}
          animateRows
          masterDetail
          detailRowAutoHeight
          isRowMaster={(row) => Boolean(row?.roomChanges?.length)}
          detailCellRenderer={MoveDetailsRenderer}
          sideBar={columnsOnlySideBar}
          suppressScrollOnNewData
          tooltipShowDelay={150}
          components={{
            ArticleRenderer,
            DetailsRenderer,
            ChangeReasonRenderer,
            ChangeValueRenderer,
            ChangeTypeRenderer,
            DisciplineRenderer,
            OccurrenceIdRenderer,
            RoomNumberRenderer,
          }}
          overlayNoRowsTemplate="<span style='padding:8px;display:inline-block;'>Ingen endringer funnet</span>"
        />
      </Box>

      <Group justify="space-between" align="center" mt="sm" wrap="nowrap">
        {onLimitChange ? (
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" c="dimmed">
              Feltendringer per side
            </Text>
            <Select
              size="xs"
              w={90}
              value={String(pagination.limit)}
              data={["50", "100", "250", "500"]}
              disabled={!enabled || isFetching}
              onChange={(value) => {
                const nextLimit = Number(value);
                if (Number.isFinite(nextLimit) && nextLimit > 0) {
                  onLimitChange(nextLimit);
                }
              }}
            />
          </Group>
        ) : (
          <Text size="xs" c="dimmed">
            {limit} feltendringer per side
          </Text>
        )}
        <Pagination
          size="sm"
          total={pagination.totalPages}
          value={pagination.activePage}
          disabled={!enabled || isFetching}
          onChange={onPageChange}
        />
      </Group>
    </Box>
  );
};
